import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";
import z from "zod";

export function normalizeForComparison(value: string): string {
  return value.trim().toLowerCase();
}

export function useAccountFormSchema({
  requireTurnstile = true,
}: { requireTurnstile?: boolean } = {}) {
  const { t } = useLingui();

  return useMemo(() => {
    return z
      .object({
        firstName: z
          .string()
          .min(1, {
            error: t`Vänligen ange ditt förnamn`,
          })
          .max(64, {
            error: t`Max 64 bokstäver`,
          }),
        lastName: z
          .string()
          .min(1, {
            error: t`Vänligen ange ditt efternamn`,
          })
          .max(64, {
            error: t`Max 64 bokstäver`,
          }),
        email: z.email({
          error: t`Vänligen ange din e-postadress`,
        }),
        password: z
          .string({
            error: t`Vänligen ange ett lösenord`,
          })
          .min(8, {
            error: t`Minst 8 tecken`,
          })
          .max(128, {
            error: t`Max 128 tecken`,
          })
          .refine((password) => !/^\d+$/.test(password), {
            error: t`Lösenordet får inte vara enbart siffror`,
          }),
        turnstileToken: requireTurnstile
          ? z.string().min(1, {
              error: t`Identifieringen misslyckades`,
            })
          : z.string().optional(),
      })
      .superRefine((values, ctx) => {
        const normalizedPassword = normalizeForComparison(values.password);
        const normalizedEmail = normalizeForComparison(values.email);
        const normalizedFirstName = normalizeForComparison(values.firstName);
        const normalizedLastName = normalizeForComparison(values.lastName);
        const normalizedFullName = normalizeForComparison(
          `${values.firstName} ${values.lastName}`
        );

        if (
          normalizedPassword === normalizedEmail ||
          normalizedPassword === normalizedFirstName ||
          normalizedPassword === normalizedLastName ||
          normalizedPassword === normalizedFullName
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t`Lösenordet får inte matcha ditt namn eller din e-post`,
          });
        }
      });
  }, [t, requireTurnstile]);
}

export type AccountFormSchema = z.infer<
  ReturnType<typeof useAccountFormSchema>
>;
